/**
 * Brand marks.
 *
 * WHY THESE ARE NOT FROM lucide-react. Lucide is an icon set, not a logo set,
 * and deliberately ships no third-party brand marks. Drawing one by hand gets
 * you what this codebase had: a generic speech bubble standing in for WhatsApp
 * on the button that sends a WhatsApp message. A reader does not decode a
 * speech bubble as "WhatsApp" — they decode it as "message", which is the one
 * thing the label already said.
 *
 * The path below is the actual WhatsApp glyph as published by Simple Icons,
 * which releases its marks into the public domain (CC0-1.0). It is a FILLED
 * mark on a 24x24 grid, so unlike the rest of the kit's icons it takes
 * `fill="currentColor"` and no stroke, and it carries WhatsApp's own green
 * rather than inheriting the button's ink. A mark redrawn in someone else's
 * palette stops being recognisable as that brand, which is the whole reason it
 * is here instead of a speech bubble.
 *
 * USING SOMEBODY'S MARK IS NOT THE SAME AS BEING ALLOWED TO. CC0 covers the
 * drawing, not WhatsApp's trademark. It is correct here because it labels a
 * genuine integration — this really does send the code over WhatsApp — and that
 * is nominative use. It would not be correct as decoration, or anywhere that
 * implied WhatsApp endorses this product.
 */

import { type ComponentProps } from "react"

import { cn } from "./cn"

/**
 * THE BRAND MARK. ONE DRAWING, EVERYWHERE.
 *
 * A shield, because the product's promise is that an expert stands between a
 * person and a tax notice, holding two lines of the document being guarded.
 *
 * There were FIVE of it before this: apps/web drew one on a 24 grid in its top
 * bar and another in its registration step, apps/panel drew a different one on
 * a 28 grid in its shell and a third in its not-found screen, and the kit held
 * a fifth. They had all drifted from each other. The panel's favicon carried a
 * check where the taxpayer app's carried document lines, so the two apps looked
 * like two products rather than one with two front doors.
 *
 * A favicon has to be generated from the same geometry as the thing in the
 * corner of the page, or the tab and the header disagree. One path, and every
 * consumer reads it from here.
 *
 * IF THE GEOMETRY CHANGES, regenerate the PNGs: `node scripts/generate-icons.mjs`.
 * The favicon.svg files are checked in beside it and carry the same paths.
 */
const SHIELD = "M12 2.5 20 6v6.2c0 4.6-3.3 7.9-8 9.3-4.7-1.4-8-4.7-8-9.3V6z"

/** The document lines: what is being guarded is a notice. */
const DOCUMENT_LINES = "M8.5 11h7M8.5 14.5h4.5"

function Shield({ interior, className, ...props }: ComponentProps<"svg"> & { interior: string }) {
	return (
		<svg
			className={cn("text-primary size-7 shrink-0", className)}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			{...props}
		>
			<path d={SHIELD} />
			<path d={interior} />
		</svg>
	)
}

/** The taxpayer app's mark. */
export function SwamithraMark(props: ComponentProps<"svg">) {
	return (
		<Shield
			interior={DOCUMENT_LINES}
			{...props}
		/>
	)
}

/**
 * The expert panel's mark.
 *
 * THE SAME DRAWING as the taxpayer app's. It used to carry a check instead of
 * the document lines, so the two apps had two logos and two favicons — which
 * reads as two products rather than one with two front doors. The panel is told
 * apart by its title and its "Expert panel" sub-line, not by a different logo.
 *
 * Kept as its own export so the panel's call sites do not all have to change,
 * and so there is one obvious place to reintroduce a variant if one is ever
 * genuinely wanted.
 */
export const SwamithraPanelMark = SwamithraMark

/**
 * The full Swamithra logo (mark, name and "Your Partner in Direct Tax
 * Matters") as one image, for the headers of both apps. Each app serves the
 * files from its own public folder and passes their URLs, so the panel's
 * `/panel/` base path is the app's concern, not this component's. Sized by
 * height; the width follows the image.
 *
 * Two files, one per theme: the light-theme logo's maroon lettering
 * disappears on the dark header, so the dark theme gets its own lighter
 * version. Both are in the page and the theme picks which one shows; the one
 * that is hidden is also hidden from screen readers, so the name is read once.
 */
export function SwamithraLogo({
	src,
	darkSrc,
	label,
	className,
}: {
	src: string
	darkSrc: string
	/** Which app this is, as text under the logo: "User Portal" or "Expert
	 *  Portal". The logo is the same in both apps, so this tells them apart. */
	label?: string
	className?: string
}) {
	const size = cn("h-14 w-auto shrink-0 md:h-[4.5rem]", className)
	const picture = (imageClass: string) => (
		<>
			<img
				src={src}
				alt="Swamithra"
				decoding="async"
				className={cn("block dark:hidden", imageClass)}
			/>
			<img
				src={darkSrc}
				alt="Swamithra"
				decoding="async"
				className={cn("hidden dark:block", imageClass)}
			/>
		</>
	)
	const images = picture(size)
	if (label === undefined) return images
	// THE LABEL IS TEXT UNDER THE LOGO, not part of the image: centred under
	// the tagline's column (x 40% to 98% of the image) in the tagline's gold.
	// Under the tagline the image is empty for about a third of its height; a
	// negative top margin in percent (taken from the width, 2.7x the height)
	// lifts the text to just under the tagline at every logo size, so the
	// label adds almost nothing to the header's height. The gold
	// is a mix of the warning tokens (there is no gold token, and colours are
	// only named in tokens.css), weighted per theme to match each logo file.
	return (
		<span className="inline-flex shrink-0 flex-col">
			{/* Sized by height and the image's own aspect ratio, with the images
			    filling it: a shrink-to-fit box around a bare <img> collapses
			    under the reset's `max-width: 100%`. */}
			<span className={cn("relative block aspect-[826/302]", size)}>
				{picture("h-full w-full")}
			</span>
			<span className="-mt-[10%] ml-[40%] w-[58%] text-center text-xs leading-tight font-semibold whitespace-nowrap text-[color-mix(in_srgb,var(--color-warning-border)_60%,var(--color-warning))] md:text-sm dark:text-[color-mix(in_srgb,var(--color-warning-border)_20%,var(--color-warning))]">
				{label}
			</span>
		</span>
	)
}

export function WhatsAppIcon({ className, ...props }: ComponentProps<"svg">) {
	return (
		<svg
			className={cn("text-brand-whatsapp size-5 shrink-0", className)}
			viewBox="0 0 24 24"
			fill="currentColor"
			aria-hidden="true"
			{...props}
		>
			<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
		</svg>
	)
}
